import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "donor-select" }

import ASelect, { SelectOption as ASelectOption } from 'ant-design-vue/es/select';
import AInput from 'ant-design-vue/es/input';
import {
    ref,
    useAttrs,
    watch,
    computed,
} from 'vue';
import { debounce, uniqBy, keyBy } from 'lodash';
import { CancelTokenSource } from 'axios';
import { axios, showErrorNotification } from '../../../utils';
import {
    getPatientInfo,
    getPatientRelatedList,
    getPatientsList,
    getPatientsListLightweigh,
} from '../../../api';

interface Donor {
    name: string,
    patientId: number,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DonorSelect',
  props: {
    patientId: { default: undefined },
    donorId: { default: undefined },
    disabled: { type: Boolean, default: false },
    title: { default: '' }
  },
  emits: {
    change: (payload) => typeof payload === 'number' || typeof payload === 'undefined',
},
  setup(__props: any, { emit }) {

const props = __props as { patientId: number, donorId: number, disabled: boolean, title: string }





const attrs = useAttrs();

const isLoading = ref(false);
const donors = ref<Donor[]>([]);
const relatedDonors = ref<Donor[]>([]);
let cancelTokenSource: CancelTokenSource | undefined;
const hasFocus = ref(false);
const donorsList = ref();

const defocus = () => {
    hasFocus.value = false;
};
const onDonorsListMount = () => {
    donorsList.value.focus();
};

const donorsMap = computed(() => keyBy(donors.value, 'patientId'));
const getDonorName = (id?: number) => {
    if (!id) {
        return '';
    }
    const donor = donorsMap.value[id];
    if (!donor) {
        return '';
    }
    return donor.name;
};

const onChange = (id?: number) => {
    defocus();
    emit('change', id);
};

const loadPatientsRelatedList = async (patientId: number) => {
    isLoading.value = true;
    if (!relatedDonors.value.length) {
        let patientInfo;
        try {
            const patientInfoResponse = await getPatientInfo(patientId);
            patientInfo = patientInfoResponse.data;
        } catch {
            showErrorNotification('Не удалось загрузить информацию о пациенте.');
            isLoading.value = false;
            return;
        }
        const related = !patientInfo.represent
            ? undefined
            : {
                name: patientInfo.represent.longName,
                patientId: patientInfo.represent.patientId,
            };
        let patientRelatedList;
        try {
            const patientRelatedListResponse = await getPatientRelatedList(patientId);
            patientRelatedList = patientRelatedListResponse.data;
        } catch {
            showErrorNotification('Не удалось загрузить список представителей.');
            isLoading.value = false;
            return;
        }
        patientRelatedList = patientRelatedList.map((patient) => ({
            name: `${patient.lastname} ${patient.firstname}${
                patient.middlename ? ` ${patient.middlename}` : ''
            }`,
            patientId: patient.id,
        }));
        if (!patientRelatedList.length && !related) {
            //
        } else {
            relatedDonors.value = related
                ? [related, ...patientRelatedList]
                : patientRelatedList;
        }
    }
    donors.value = uniqBy(
        [...relatedDonors.value, ...donors.value],
        (donor) => donor.patientId,
    );
    isLoading.value = false;
};

watch(
    () => props.donorId,
    async (donorId) => {
        if (!donorId) {
            return;
        }
        const donorsResponse = await getPatientsList({ params: { id: donorId } });
        donors.value = donorsResponse.data;

        await loadPatientsRelatedList(props.patientId);
    },
    { immediate: true },
);

watch(
    () => props.patientId,
    (patientId) => loadPatientsRelatedList(patientId),
    { immediate: true },
);

const loadPatientsList = debounce(async (value: string | number) => {
    if (cancelTokenSource) {
        cancelTokenSource.cancel();
    }
    if ((!value && relatedDonors.value.length) || (typeof value === 'string' && value.length < 3)) {
        donors.value = relatedDonors.value;
        return;
    }
    cancelTokenSource = axios.CancelToken.source();
    isLoading.value = true;
    try {
        const donorsResponse = await getPatientsListLightweigh({
            params: {
                query: value,
                lightweight: true,
                page: JSON.stringify([0, 10]),
            },
            cancelToken: cancelTokenSource.token,
        });
        donors.value = donorsResponse.data.data.filter(
            ({ patientId }) => patientId !== props.patientId,
        );
    } catch {
        showErrorNotification('Не удалось выполнить поиск по пациентам.');
        return;
    } finally {
        isLoading.value = false;
    }
}, 300);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("label", _hoisted_1, [
    _createElementVNode("span", null, _toDisplayString(__props.title || 'Донор '), 1),
    (__props.disabled || !hasFocus.value)
      ? (_openBlock(), _createBlock(_unref(AInput), _mergeProps({ key: 0 }, _ctx.$attrs, {
          disabled: __props.disabled,
          value: getDonorName(__props.donorId),
          placeholder: "Нет",
          onFocus: _cache[0] || (_cache[0] = ($event: any) => (hasFocus.value = true))
        }), null, 16, ["disabled", "value"]))
      : (_openBlock(), _createBlock(_unref(ASelect), _mergeProps({
          key: 1,
          ref_key: "donorsList",
          ref: donorsList
        }, _unref(attrs), {
          "default-open": "",
          "show-search": "",
          value: __props.donorId,
          "dropdown-match-select-width": false,
          "filter-option": false,
          "allow-clear": "",
          loading: isLoading.value,
          onSearch: _unref(loadPatientsList),
          onChange: onChange,
          onVueMounted: onDonorsListMount
        }), {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(donors.value, (donor) => {
              return (_openBlock(), _createBlock(_unref(ASelectOption), {
                key: donor.patientId
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(donor.name), 1)
                ]),
                _: 2
              }, 1024))
            }), 128))
          ]),
          _: 1
        }, 16, ["value", "loading", "onSearch"]))
  ]))
}
}

})